import { Box, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { McpServerItemComponent } from '../../components/mcp-server-item/mcp-server-item.component';
import { useRequest } from '../../hooks/useRequest';
import {
  AdminMcpServer,
  fetchMcpServers,
  McpServerStatus,
  restartMcpServer,
} from '../../utils/axios/api';

export function AdminMcpPage() {
  const { t } = useTranslation();
  const loadRequest = useRequest<AdminMcpServer[]>();
  const restartRequest = useRequest();
  const [servers, setServers] = useState<AdminMcpServer[]>([]);

  const loadServers = useCallback(async () => {
    const data = await loadRequest.fetch(() => fetchMcpServers());
    if (!data) {
      return null;
    }
    setServers(data);
    return data;
  }, [loadRequest]);

  useEffect(() => {
    void loadServers();
  }, [loadServers]);

  const handleRestart = useCallback(
    async (id: string) => {
      setServers((current) =>
        current.map((server) =>
          server.id === id
            ? { ...server, status: McpServerStatus.Restarting, error: null }
            : server,
        ),
      );

      const result = await restartRequest.fetch(async () => {
        await restartMcpServer(id);
        await loadServers();
      });
      if (result === null) {
        await loadServers();
      }
    },
    [loadServers, restartRequest],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        p: 2,
      }}
    >
      <Typography variant="subtitle1">{t('admin.mcp.description')}</Typography>
      {loadRequest.isLoading && servers.length === 0 ? (
        <CircularProgress size={24} />
      ) : (restartRequest.exception ?? loadRequest.exception) &&
        servers.length === 0 ? (
        <Typography variant="body2" color="error">
          {t(
            `exceptions.${restartRequest.exception ?? loadRequest.exception}`,
          )}
        </Typography>
      ) : servers.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('admin.mcp.empty')}
        </Typography>
      ) : (
        <>
          {restartRequest.exception ?? loadRequest.exception ? (
            <Typography variant="body2" color="error">
              {t(
                `exceptions.${restartRequest.exception ?? loadRequest.exception}`,
              )}
            </Typography>
          ) : null}
          {servers.map((server) => (
            <McpServerItemComponent
              key={server.id}
              server={server}
              onRestart={() => {
                void handleRestart(server.id);
              }}
            />
          ))}
        </>
      )}
    </Box>
  );
}
